// src/pages/calidad/BpmReporte.jsx
// KPI 5.1 Cumplimiento a BPMs — pestañas Resumen / Histórico / Análisis / Historial de incidencias.
// % = rubros que cumplen ÷ (personas evaluadas × rubros del catálogo activo), día por día,
// con el mismo cálculo del Resumen General (services/bpmCumplimiento.js).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import * as XLSX from 'xlsx';
import { FiDownload, FiInfo, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import api from '../../services/api';
import s from '../mantenimiento/estilos';
import BpmFichaPersona, { TagNivel } from './BpmFichaPersona';

Chart.register(...registerables);

const BASE = '/api/calidad/bpm-analisis';
const COLOR = { verde: '#3E7C5A', amarillo: '#B8862E', rojo: '#B23A48', morado: '#4A1E5C', lila: '#A98BB8', gris: '#8A7690', azul: '#4F7CA8', naranja: '#B2662A' };

const num = (v) => (v === null || v === undefined || v === '' ? null : parseFloat(v));
const fmtPct = (v) => (num(v) === null ? '—' : `${num(v).toFixed(1)}%`);
const fmtFecha = (d) => (d ? new Date(String(d).length === 10 ? `${d}T00:00:00` : d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const pad = (n) => String(n).padStart(2, '0');
const aISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const nombreMes = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
const etiquetaSemana = (iso) => `Sem. ${new Date(`${iso}T00:00:00`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}`;

function semaforo(v, meta) {
  if (num(v) === null) return 'gris';
  if (num(v) >= meta) return 'verde';
  if (num(v) >= meta - 10) return 'amarillo';
  return 'rojo';
}

function rangosRapidos() {
  const h = new Date();
  const mesIni = new Date(h.getFullYear(), h.getMonth(), 1);
  const mesAntIni = new Date(h.getFullYear(), h.getMonth() - 1, 1);
  const mesAntFin = new Date(h.getFullYear(), h.getMonth(), 0);
  const tri = Math.floor(h.getMonth() / 3) * 3;
  const hace90 = new Date(h); hace90.setDate(h.getDate() - 90);
  return {
    mes: { label: 'Este mes', desde: aISO(mesIni), hasta: aISO(h) },
    mes_anterior: { label: 'Mes anterior', desde: aISO(mesAntIni), hasta: aISO(mesAntFin) },
    trimestre: { label: 'Trimestre actual', desde: aISO(new Date(h.getFullYear(), tri, 1)), hasta: aISO(h) },
    dias90: { label: 'Últimos 90 días', desde: aISO(hace90), hasta: aISO(h) },
    anio: { label: 'Este año', desde: `${h.getFullYear()}-01-01`, hasta: aISO(h) },
  };
}

function Grafica({ config, alto = 260 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = new Chart(ref.current, config);
    return () => chart.destroy();
  }, [config]);
  return <div style={{ position: 'relative', height: alto }}><canvas ref={ref} /></div>;
}

function barrasHorizontales(filas, etiqueta, valor, color, alto) {
  return {
    type: 'bar',
    data: {
      labels: filas.map((r) => { const t = etiqueta(r); return t.length > 30 ? `${t.slice(0, 29)}…` : t; }),
      datasets: [{ label: 'Incidencias', data: filas.map(valor), backgroundColor: color }],
    },
    options: { indexAxis: 'y', maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { precision: 0 } } } },
    alto,
  };
}

export default function BpmReporte() {
  const rapidos = useMemo(rangosRapidos, []);
  const [tab, setTab] = useState('resumen');
  const [semanas, setSemanas] = useState([]);
  const [meses, setMeses] = useState([]);
  const [meta, setMeta] = useState(95);
  const [dias, setDias] = useState([]);
  const [topMes, setTopMes] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [periodoHist, setPeriodoHist] = useState('semana');
  const [fichaId, setFichaId] = useState(null);

  // Análisis
  const [rango, setRango] = useState(rapidos.mes);
  const [turnoAnalisis, setTurnoAnalisis] = useState('');
  const [analisis, setAnalisis] = useState(null);
  const [cargandoAnalisis, setCargandoAnalisis] = useState(false);

  // Historial
  const filtrosVacios = { desde: rapidos.mes.desde, hasta: rapidos.mes.hasta, turno: '', area: '', nivel: '', busqueda: '' };
  const [filtros, setFiltros] = useState(filtrosVacios);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(50);
  const [historial, setHistorial] = useState({ data: [], total: 0, totalPaginas: 1 });
  const [cargandoHist, setCargandoHist] = useState(false);
  const [exportando, setExportando] = useState(false);

  async function cargarAnalisis(r = rango, t = turnoAnalisis) {
    try {
      setCargandoAnalisis(true);
      const res = await api.get(`${BASE}/top`, { params: { desde: r.desde, hasta: r.hasta, turno: t || undefined } });
      setAnalisis(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setCargandoAnalisis(false);
    }
  }

  async function cargarHistorial(f = filtros, p = pagina, pp = porPagina) {
    try {
      setCargandoHist(true);
      const params = { pagina: p, porPagina: pp };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(`${BASE}/incidencias`, { params });
      setHistorial({ data: res.data.data, total: res.data.total, totalPaginas: res.data.totalPaginas });
    } catch (err) {
      console.error(err);
    } finally {
      setCargandoHist(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        setCargando(true);
        const hoy = new Date();
        const hace13 = new Date(hoy); hace13.setDate(hoy.getDate() - 13);
        const [sem, mes, ds, top] = await Promise.all([
          api.get(`${BASE}/periodos`, { params: { periodo: 'semana', n: 10 } }),
          api.get(`${BASE}/periodos`, { params: { periodo: 'mes', n: 6 } }),
          api.get(`${BASE}/dias`, { params: { desde: aISO(hace13), hasta: aISO(hoy) } }),
          api.get(`${BASE}/top`, { params: rapidos.mes }),
        ]);
        setSemanas(sem.data.data);
        setMeses(mes.data.data);
        setMeta(sem.data.meta ?? 95);
        setDias(ds.data.data);
        setTopMes(top.data.data);
        setAnalisis(top.data.data);
        await cargarHistorial(filtrosVacios, 1, porPagina);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('No se pudo cargar el KPI de Cumplimiento a BPMs. Revisa que la ruta /api/calidad/bpm-analisis esté montada.');
      } finally {
        setCargando(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function irAHistorial(extra) {
    const f = { ...filtrosVacios, ...extra };
    setFiltros(f);
    setPagina(1);
    setTab('historial');
    cargarHistorial(f, 1);
  }

  // ─── Derivados ───
  const semCurso = semanas.find((p) => p.es_parcial) || null;
  const semCerradas = semanas.filter((p) => !p.es_parcial && p.porcentaje !== null);
  const semCerrada = semCerradas[semCerradas.length - 1] || null;
  const semPrev = semCerradas[semCerradas.length - 2] || null;
  const mesCerrados = meses.filter((p) => !p.es_parcial && p.porcentaje !== null);
  const mesCerrado = mesCerrados[mesCerrados.length - 1] || null;
  const mesPrev = mesCerrados[mesCerrados.length - 2] || null;
  const mesCurso = meses.find((p) => p.es_parcial) || null;

  const serie = periodoHist === 'mes' ? meses : semanas;
  const etiqueta = (p) => (periodoHist === 'mes'
    ? new Date(`${p.desde}T00:00:00`).toLocaleDateString('es-MX', { month: 'short', year: '2-digit' })
    : new Date(`${p.desde}T00:00:00`).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })) + (p.es_parcial ? '*' : '');

  const configDias = useMemo(() => ({
    type: 'bar',
    data: {
      labels: dias.map((d) => new Date(`${d.fecha}T00:00:00`).toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit' })),
      datasets: [
        { type: 'line', label: '% del día (combinado)', data: dias.map((d) => d.porcentaje), borderColor: COLOR.morado, backgroundColor: COLOR.morado, yAxisID: 'y', tension: 0.25, pointRadius: 4, spanGaps: true },
        { label: 'Fallas turno día', data: dias.map((d) => d.dia.fallidos), backgroundColor: '#E8C77A', yAxisID: 'y2', stack: 'f' },
        { label: 'Fallas turno noche', data: dias.map((d) => d.noche.fallidos), backgroundColor: COLOR.lila, yAxisID: 'y2', stack: 'f' },
      ],
    },
    options: {
      maintainAspectRatio: false,
      scales: {
        y: { position: 'left', suggestedMin: 80, max: 100, ticks: { callback: (v) => `${v}%` } },
        y2: { position: 'right', beginAtZero: true, grid: { drawOnChartArea: false }, ticks: { precision: 0 }, title: { display: true, text: 'Incidencias' } },
      },
      plugins: { legend: { position: 'bottom' } },
    },
  }), [dias]);

  const configPct = useMemo(() => ({
    type: 'line',
    data: {
      labels: serie.map(etiqueta),
      datasets: [
        { label: '% cumplimiento', data: serie.map((p) => p.porcentaje), borderColor: COLOR.morado, backgroundColor: COLOR.morado, borderWidth: 2.5, pointRadius: 5, tension: 0.25, spanGaps: true,
          pointBackgroundColor: serie.map((p) => (p.es_parcial ? '#fff' : COLOR[semaforo(p.porcentaje, meta)])) },
        { label: `Meta (${meta}%)`, data: serie.map(() => meta), borderColor: COLOR.verde, borderDash: [6, 4], borderWidth: 1.5, pointRadius: 0 },
      ],
    },
    options: { maintainAspectRatio: false, scales: { y: { suggestedMin: 80, max: 100, ticks: { callback: (v) => `${v}%` } } }, plugins: { legend: { position: 'bottom' } } },
  }), [serie, meta]); // eslint-disable-line react-hooks/exhaustive-deps

  const configIncidencias = useMemo(() => ({
    type: 'bar',
    data: {
      labels: serie.map(etiqueta),
      datasets: [
        { label: 'Riesgo ALTO', data: serie.map((p) => p.incidencias_alto), backgroundColor: COLOR.rojo, stack: 'i' },
        { label: 'Riesgo MEDIO / BAJO', data: serie.map((p) => p.incidencias - p.incidencias_alto), backgroundColor: '#E8C77A', stack: 'i' },
      ],
    },
    options: { maintainAspectRatio: false, scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } } }, plugins: { legend: { position: 'bottom' } } },
  }), [serie]); // eslint-disable-line react-hooks/exhaustive-deps

  const graficasAnalisis = useMemo(() => {
    if (!analisis) return null;
    const areas = analisis.areas.slice(0, 10);
    const incs = analisis.incidencias.slice(0, 10);
    return {
      areas: barrasHorizontales(areas, (r) => r.area, (r) => r.incidencias, COLOR.morado, Math.max(180, areas.length * 32 + 40)),
      incidencias: barrasHorizontales(incs, (r) => r.incidencia, (r) => r.veces, incs.map((r) => (r.nivel_riesgo === 'ALTO' ? COLOR.rojo : r.nivel_riesgo === 'MEDIO' ? COLOR.naranja : COLOR.verde)), Math.max(180, incs.length * 32 + 40)),
      niveles: {
        type: 'doughnut',
        data: {
          labels: ['ALTO', 'MEDIO', 'BAJO'],
          datasets: [{ data: ['ALTO', 'MEDIO', 'BAJO'].map((n) => analisis.niveles.find((x) => x.nivel === n)?.incidencias || 0), backgroundColor: [COLOR.rojo, COLOR.naranja, COLOR.verde] }],
        },
        options: { maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } },
      },
      turnos: {
        type: 'bar',
        data: {
          labels: ['Día', 'Noche'],
          datasets: [{ label: 'Incidencias', data: ['dia', 'noche'].map((t) => analisis.turnos.find((x) => x.turno === t)?.incidencias || 0), backgroundColor: ['#E8C77A', COLOR.lila] }],
        },
        options: { maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } },
      },
    };
  }, [analisis]);

  // ─── Excel ───
  function excelHistorico() {
    const wb = XLSX.utils.book_new();
    const hoja = (lista, esMes) => XLSX.utils.json_to_sheet(lista.map((p) => ({
      Periodo: esMes ? nombreMes(p.desde) : `${fmtFecha(p.desde)} – ${fmtFecha(p.hasta)}`,
      '% cumplimiento': p.porcentaje, Meta: meta, 'Rubros evaluados': p.total_rubros_evaluados, 'Rubros fallidos': p.rubros_fallidos,
      Incidencias: p.incidencias, 'Incidencias riesgo ALTO': p.incidencias_alto, 'Personas con incidencia': p.personas_con_incidencia,
      'Días con datos': p.dias_con_datos, Parcial: p.es_parcial ? 'Sí' : '',
    })));
    XLSX.utils.book_append_sheet(wb, hoja(semanas, false), 'Por semana');
    XLSX.utils.book_append_sheet(wb, hoja(meses, true), 'Por mes');
    XLSX.writeFile(wb, `BPM_historico_${aISO(new Date())}.xlsx`);
  }

  function excelAnalisis() {
    if (!analisis) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(analisis.personas.map((p) => ({ Persona: p.nombre, Área: p.area, Incidencias: p.incidencias, Puntos: p.puntos, 'Riesgo ALTO': p.alto, 'Última incidencia': fmtFecha(p.ultima), 'Agregada a mano': p.manual ? 'Sí' : '' }))), 'Personas');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(analisis.areas.map((a) => ({ Área: a.area, Incidencias: a.incidencias, Puntos: a.puntos, Personas: a.personas, 'Riesgo ALTO': a.alto }))), 'Áreas');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(analisis.incidencias.map((i) => ({ Incidencia: i.incidencia, Nivel: i.nivel_riesgo, Puntos: i.puntuacion, Veces: i.veces, Personas: i.personas }))), 'Incidencias');
    XLSX.writeFile(wb, `BPM_analisis_${rango.desde}_a_${rango.hasta}.xlsx`);
  }

  async function excelHistorial() {
    try {
      setExportando(true);
      const params = { exportar: '1' };
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(`${BASE}/incidencias`, { params });
      const ws = XLSX.utils.json_to_sheet(res.data.data.map((c) => ({
        Fecha: fmtFecha(c.fecha), Turno: c.turno === 'dia' ? 'Día' : 'Noche', Persona: c.empleado_nombre, Área: c.area,
        Incidencia: c.incidencia, Nivel: c.nivel_riesgo, Puntos: c.puntuacion, Observaciones: c.observaciones || '', 'Agregada a mano': c.manual ? 'Sí' : '',
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Incidencias');
      XLSX.writeFile(wb, `BPM_incidencias_${filtros.desde || 'inicio'}_a_${filtros.hasta || 'hoy'}.xlsx`);
    } catch (err) {
      console.error(err);
    } finally {
      setExportando(false);
    }
  }

  if (cargando) return <div style={s.paginaContenedor}>Cargando (calcula el % día por día con Asistencias)...</div>;
  if (error) return <div style={{ ...s.paginaContenedor, color: COLOR.rojo }}>{error}</div>;

  const areasConocidas = (topMes?.areas || []).map((a) => a.area);

  return (
    <div style={s.paginaContenedor} className="contenedor-captura">
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>5.1 Cumplimiento a BPMs</h1>
        <div style={{ fontSize: 12, color: COLOR.gris }}>Rubros dentro de especificación ÷ rubros evaluados · meta {meta}%</div>
      </div>

      <div style={s.tabBar}>
        {[['resumen', 'Resumen'], ['historico', 'Histórico'], ['analisis', 'Personas y áreas'], ['historial', 'Historial de incidencias']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...s.tabBtn, ...(tab === k ? s.tabBtnActivo : {}) }}>{label}</button>
        ))}
      </div>

      {/* ═══════════ RESUMEN ═══════════ */}
      {tab === 'resumen' && (
        <>
          <div style={s.kgrid}>
            <TarjetaPct titulo={semCerrada ? `Semana cerrada · ${etiquetaSemana(semCerrada.desde)}` : 'Semana cerrada'} p={semCerrada} previo={semPrev} meta={meta} principal etiquetaPrevio="semana anterior" />
            <TarjetaPct titulo={mesCerrado ? `Mes cerrado · ${nombreMes(mesCerrado.desde)}` : 'Mes cerrado'} p={mesCerrado} previo={mesPrev} meta={meta} etiquetaPrevio="mes anterior" />
            <div style={{ ...s.kcard, borderTop: `3px dashed ${COLOR.gris}` }}>
              <div style={s.kLbl}>Semana en curso {semCurso && `· ${etiquetaSemana(semCurso.desde)}`}</div>
              <div style={{ ...s.kVal, fontSize: 30, color: '#2A1830' }}>{fmtPct(semCurso?.porcentaje)}</div>
              <div style={s.kSub}>{semCurso ? `${semCurso.incidencias} incidencias · ${semCurso.dias_con_datos} días con datos (parcial)` : ''}</div>
            </div>
            <div style={{ ...s.kcard, borderTop: `3px solid ${COLOR.rojo}`, cursor: 'pointer' }} onClick={() => irAHistorial({ ...rapidos.mes })}>
              <div style={s.kLbl}>Incidencias este mes</div>
              <div style={{ ...s.kVal, fontSize: 30, color: COLOR.rojo }}>{mesCurso?.incidencias ?? 0}</div>
              <div style={s.kSub}>{mesCurso ? `${mesCurso.personas_con_incidencia} personas · ${mesCurso.incidencias_alto} de riesgo ALTO. Clic para verlas.` : ''}</div>
            </div>
          </div>

          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>Últimos 14 días</div>
            <Grafica config={configDias} alto={250} />
          </div>

          {topMes && (
            <div style={s.grid2}>
              <div style={s.tarjeta}>
                <div style={s.seccionTitulo}>Personas con más puntos este mes</div>
                <TablaPersonas filas={topMes.personas.slice(0, 6)} onAbrir={setFichaId} />
              </div>
              <div style={s.tarjeta}>
                <div style={s.seccionTitulo}>Áreas con más incidencias este mes</div>
                <TablaAreas filas={topMes.areas.slice(0, 6)} onAbrir={(area) => irAHistorial({ ...rapidos.mes, area })} />
              </div>
            </div>
          )}

          <div style={s.seccionKpiBloque}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <FiInfo style={{ color: COLOR.morado, marginTop: 2, flexShrink: 0 }} />
              <div style={s.seccionKpiFormula}>
                <strong style={{ color: '#2A1830' }}>Cómo se calcula.</strong> Cada persona evaluada en un turno revisa todos los rubros activos del catálogo.
                % = rubros que cumplen ÷ (personas evaluadas × rubros del catálogo). Las personas evaluadas son las que checaron en Asistencias ese día y turno,
                más las agregadas a mano que tuvieron alguna incidencia. La semana y el mes suman todos sus días. Meta: {meta}% o más.
                <br />
                <strong style={{ color: '#2A1830' }}>Puntos y suspensiones.</strong> Cada incidencia suma puntos según su nivel de riesgo; al llegar a 12 en el trimestre se genera una suspensión.
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════ HISTÓRICO ═══════════ */}
      {tab === 'historico' && (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center' }}>
            {[['semana', 'Por semana'], ['mes', 'Por mes']].map(([k, label]) => (
              <button key={k} onClick={() => setPeriodoHist(k)} style={{ ...s.subTabBtn, ...(periodoHist === k ? s.subTabBtnActivo : {}) }}>{label}</button>
            ))}
            <button onClick={excelHistorico} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}><FiDownload /> Excel</button>
          </div>
          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>% de cumplimiento</div>
            <Grafica config={configPct} />
            <div style={{ ...s.kSub, marginTop: 8 }}>* Periodo en curso, parcial.</div>
          </div>
          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>Incidencias capturadas</div>
            <Grafica config={configIncidencias} alto={230} />
          </div>
          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>{periodoHist === 'mes' ? 'Mes' : 'Semana'}</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>% cumplimiento</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Rubros evaluados</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Incidencias</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Riesgo ALTO</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Personas</th>
                </tr>
              </thead>
              <tbody>
                {[...serie].reverse().map((p) => (
                  <tr key={p.desde} style={{ cursor: 'pointer' }} onClick={() => irAHistorial({ desde: p.desde, hasta: p.hasta })}>
                    <td style={s.td}>{periodoHist === 'mes' ? nombreMes(p.desde) : `${fmtFecha(p.desde)} – ${fmtFecha(p.hasta)}`}{p.es_parcial && <span style={{ ...x.tag, color: '#7a5417', background: '#F9F0DD', marginLeft: 6 }}>parcial</span>}</td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: p.es_parcial ? COLOR.gris : COLOR[semaforo(p.porcentaje, meta)] }}>{fmtPct(p.porcentaje)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{p.total_rubros_evaluados.toLocaleString('es-MX')}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{p.incidencias}</td>
                    <td style={{ ...s.td, textAlign: 'right', color: p.incidencias_alto ? COLOR.rojo : undefined }}>{p.incidencias_alto}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{p.personas_con_incidencia}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════════ PERSONAS Y ÁREAS ═══════════ */}
      {tab === 'analisis' && (
        <>
          <div style={{ ...s.filtrosBar, alignItems: 'center' }}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Periodo</span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.entries(rapidos).map(([k, r]) => (
                  <button key={k} onClick={() => { setRango(r); cargarAnalisis(r); }} style={{ ...s.subTabBtn, ...(rango.desde === r.desde && rango.hasta === r.hasta ? s.subTabBtnActivo : {}) }}>{r.label}</button>
                ))}
              </div>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Desde</span>
              <input type="date" style={s.filtroInput} value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value, label: 'Rango' })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Hasta</span>
              <input type="date" style={s.filtroInput} value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value, label: 'Rango' })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Turno</span>
              <select style={s.filtroInput} value={turnoAnalisis} onChange={(e) => { setTurnoAnalisis(e.target.value); cargarAnalisis(rango, e.target.value); }}>
                <option value="">Ambos</option>
                <option value="dia">Día</option>
                <option value="noche">Noche</option>
              </select>
            </div>
            <button style={s.botonBuscar2} onClick={() => cargarAnalisis()}>Ver</button>
            <button onClick={excelAnalisis} disabled={!analisis} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}><FiDownload /> Excel</button>
          </div>

          {cargandoAnalisis && <div style={s.subtitulo}>Cargando...</div>}
          {!cargandoAnalisis && analisis && (
            <>
              <div style={s.kgrid}>
                <Conteo titulo="Incidencias" valor={analisis.totales.incidencias} color={COLOR.rojo} />
                <Conteo titulo="Personas con incidencia" valor={analisis.totales.personas} color={COLOR.morado} />
                <Conteo titulo="Puntos acumulados" valor={analisis.totales.puntos} color={COLOR.naranja} />
                <Conteo titulo="Área con más incidencias" valor={analisis.areas[0]?.area || '—'} sub={analisis.areas[0] ? `${analisis.areas[0].incidencias} incidencias` : ''} color={COLOR.azul} texto />
              </div>
              {analisis.totales.incidencias === 0 ? (
                <div style={s.subtitulo}>Sin incidencias en este periodo.</div>
              ) : (
                <>
                  <div style={s.grid2}>
                    <div style={s.tarjeta}>
                      <div style={s.seccionTitulo}>Áreas con más incidencias</div>
                      <Grafica config={graficasAnalisis.areas} alto={graficasAnalisis.areas.alto} />
                    </div>
                    <div style={s.tarjeta}>
                      <div style={s.seccionTitulo}>Incidencias más frecuentes</div>
                      <Grafica config={graficasAnalisis.incidencias} alto={graficasAnalisis.incidencias.alto} />
                    </div>
                  </div>
                  <div style={s.grid2}>
                    <div style={s.tarjeta}>
                      <div style={s.seccionTitulo}>Por nivel de riesgo</div>
                      <Grafica config={graficasAnalisis.niveles} alto={220} />
                    </div>
                    <div style={s.tarjeta}>
                      <div style={s.seccionTitulo}>Por turno</div>
                      <Grafica config={graficasAnalisis.turnos} alto={220} />
                    </div>
                  </div>
                  <div style={s.grid2}>
                    <div style={s.tarjeta}>
                      <div style={s.seccionTitulo}>Personas con más puntos</div>
                      <TablaPersonas filas={analisis.personas} onAbrir={setFichaId} />
                    </div>
                    <div style={s.tarjeta}>
                      <div style={s.seccionTitulo}>Áreas</div>
                      <TablaAreas filas={analisis.areas} onAbrir={(area) => irAHistorial({ desde: rango.desde, hasta: rango.hasta, area })} />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ═══════════ HISTORIAL ═══════════ */}
      {tab === 'historial' && (
        <>
          <div style={s.filtrosBar}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Desde</span>
              <input type="date" style={s.filtroInput} value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Hasta</span>
              <input type="date" style={s.filtroInput} value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Turno</span>
              <select style={s.filtroInput} value={filtros.turno} onChange={(e) => setFiltros({ ...filtros, turno: e.target.value })}>
                <option value="">Ambos</option>
                <option value="dia">Día</option>
                <option value="noche">Noche</option>
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Área</span>
              <select style={s.filtroInput} value={filtros.area} onChange={(e) => setFiltros({ ...filtros, area: e.target.value })}>
                <option value="">Todas</option>
                {[...new Set([...areasConocidas, filtros.area].filter(Boolean))].map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Riesgo</span>
              <select style={s.filtroInput} value={filtros.nivel} onChange={(e) => setFiltros({ ...filtros, nivel: e.target.value })}>
                <option value="">Todos</option>
                <option value="ALTO">ALTO</option>
                <option value="MEDIO">MEDIO</option>
                <option value="BAJO">BAJO</option>
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Buscar</span>
              <input type="text" placeholder="Persona, área, incidencia…" style={{ ...s.filtroInput, minWidth: 190 }} value={filtros.busqueda}
                onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPagina(1); cargarHistorial(filtros, 1); } }} />
            </div>
            <button style={s.botonBuscar2} onClick={() => { setPagina(1); cargarHistorial(filtros, 1); }}>Buscar</button>
            <button style={s.linkChico} onClick={() => { setFiltros(filtrosVacios); setPagina(1); cargarHistorial(filtrosVacios, 1); }}>Limpiar</button>
          </div>

          <div style={{ ...s.headerFila, marginBottom: 8 }}>
            <div style={{ fontSize: 12, color: COLOR.gris }}>{cargandoHist ? 'Cargando...' : `${historial.total.toLocaleString('es-MX')} incidencias`} · clic en la persona para ver su historial</div>
            <button onClick={excelHistorial} disabled={exportando || historial.total === 0} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiDownload /> {exportando ? 'Generando...' : 'Descargar Excel'}
            </button>
          </div>

          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Fecha</th>
                  <th style={s.th}>Turno</th>
                  <th style={s.th}>Persona</th>
                  <th style={s.th}>Área</th>
                  <th style={s.th}>Incidencia</th>
                  <th style={s.th}>Nivel</th>
                </tr>
              </thead>
              <tbody>
                {!cargandoHist && historial.data.length === 0 && <tr><td colSpan={6} style={{ ...s.td, color: COLOR.gris, padding: 16 }}>Sin incidencias con estos filtros.</td></tr>}
                {historial.data.map((c) => (
                  <tr key={c.id}>
                    <td style={s.td}>{fmtFecha(c.fecha)}</td>
                    <td style={s.td}>{c.turno === 'dia' ? 'Día' : 'Noche'}</td>
                    <td style={s.td}>
                      <button style={x.link} onClick={() => setFichaId(c.empleado_id)}>{c.empleado_nombre}</button>
                      {c.manual && <div style={x.sub}>agregada a mano</div>}
                    </td>
                    <td style={s.td}>{c.area}</td>
                    <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 200 }}>
                      {c.incidencia}
                      {c.observaciones && <div style={{ ...x.sub, fontStyle: 'italic' }}>{c.observaciones}</div>}
                    </td>
                    <td style={s.td}><TagNivel nivel={c.nivel_riesgo} puntos={c.puntuacion} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={x.paginacion}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={s.filtroLabel}>Por página</span>
              <select style={s.inputChico} value={porPagina} onChange={(e) => { const pp = Number(e.target.value); setPorPagina(pp); setPagina(1); cargarHistorial(filtros, 1, pp); }}>
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={x.botonPagina} disabled={pagina <= 1 || cargandoHist} onClick={() => { const p = pagina - 1; setPagina(p); cargarHistorial(filtros, p); }} aria-label="Página anterior"><FiChevronLeft /></button>
              <span style={{ fontSize: 12 }}>Página {pagina} de {historial.totalPaginas}</span>
              <button style={x.botonPagina} disabled={pagina >= historial.totalPaginas || cargandoHist} onClick={() => { const p = pagina + 1; setPagina(p); cargarHistorial(filtros, p); }} aria-label="Página siguiente"><FiChevronRight /></button>
            </div>
          </div>
        </>
      )}

      <BpmFichaPersona empleadoId={fichaId} onCerrar={() => setFichaId(null)} />
    </div>
  );
}

function TarjetaPct({ titulo, p, previo, meta, principal, etiquetaPrevio }) {
  const v = num(p?.porcentaje);
  const color = principal ? COLOR[semaforo(v, meta)] : COLOR.morado;
  const pv = num(previo?.porcentaje);
  const d = v !== null && pv !== null ? v - pv : null;
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${color}` }}>
      <div style={s.kLbl}>{titulo}</div>
      <div style={{ ...s.kVal, fontSize: 32, color }}>{fmtPct(v)}</div>
      {principal && v !== null && (
        <div style={{ fontSize: 12.5, fontWeight: 600, margin: '6px 0', color }}>
          {v >= meta ? `Cumple la meta de ${meta}%` : `No cumple la meta de ${meta}% (${(v - meta).toFixed(1)} pts)`}
        </div>
      )}
      <div style={s.kSub}>{p ? `${p.incidencias} incidencias · ${p.rubros_fallidos} de ${p.total_rubros_evaluados.toLocaleString('es-MX')} rubros fallaron` : 'Sin datos'}</div>
      {d !== null && (
        <div style={{ ...s.kSub, color: d > 0 ? COLOR.verde : d < 0 ? COLOR.rojo : COLOR.gris }}>
          {d > 0 ? '▲' : d < 0 ? '▼' : '='} {Math.abs(d).toFixed(1)} pts vs {etiquetaPrevio} ({fmtPct(pv)})
        </div>
      )}
    </div>
  );
}

const Conteo = ({ titulo, valor, sub, color, texto }) => (
  <div style={{ ...s.kcard, borderTop: `3px solid ${color}` }}>
    <div style={s.kLbl}>{titulo}</div>
    <div style={{ ...s.kVal, fontSize: texto ? 18 : 30, color, marginTop: texto ? 6 : 0 }}>{valor}</div>
    {sub && <div style={s.kSub}>{sub}</div>}
  </div>
);

function TablaPersonas({ filas, onAbrir }) {
  if (!filas.length) return <div style={{ fontSize: 12, color: COLOR.gris }}>Sin incidencias.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={s.tabla}>
        <thead>
          <tr>
            <th style={s.th}>Persona</th>
            <th style={{ ...s.th, textAlign: 'right' }}>Inc.</th>
            <th style={{ ...s.th, textAlign: 'right' }}>Puntos</th>
            <th style={{ ...s.th, textAlign: 'right' }}>ALTO</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((p) => (
            <tr key={p.empleado_id}>
              <td style={{ ...s.td, whiteSpace: 'normal' }}>
                <button style={x.link} onClick={() => onAbrir(p.empleado_id)}>{p.nombre}</button>
                <div style={x.sub}>{p.area}{p.manual ? ' · agregada a mano' : ''}</div>
              </td>
              <td style={{ ...s.td, textAlign: 'right' }}>{p.incidencias}</td>
              <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: p.puntos >= 12 ? COLOR.rojo : p.puntos >= 8 ? COLOR.naranja : undefined }}>{p.puntos}</td>
              <td style={{ ...s.td, textAlign: 'right', color: p.alto ? COLOR.rojo : undefined }}>{p.alto}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TablaAreas({ filas, onAbrir }) {
  if (!filas.length) return <div style={{ fontSize: 12, color: COLOR.gris }}>Sin incidencias.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={s.tabla}>
        <thead>
          <tr>
            <th style={s.th}>Área</th>
            <th style={{ ...s.th, textAlign: 'right' }}>Inc.</th>
            <th style={{ ...s.th, textAlign: 'right' }}>Personas</th>
            <th style={{ ...s.th, textAlign: 'right' }}>Puntos</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((a) => (
            <tr key={a.area} style={{ cursor: 'pointer' }} onClick={() => onAbrir(a.area)}>
              <td style={{ ...s.td, whiteSpace: 'normal' }}>{a.area}</td>
              <td style={{ ...s.td, textAlign: 'right', fontWeight: 700 }}>{a.incidencias}</td>
              <td style={{ ...s.td, textAlign: 'right' }}>{a.personas}</td>
              <td style={{ ...s.td, textAlign: 'right' }}>{a.puntos}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const x = {
  tag: { display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' },
  sub: { fontSize: 10.5, color: '#8A7690' },
  link: { background: 'none', border: 'none', padding: 0, color: '#4A1E5C', fontWeight: 600, fontSize: 13, cursor: 'pointer', textAlign: 'left', textDecoration: 'underline' },
  paginacion: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  botonPagina: { width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E6DDD2', background: '#fff', borderRadius: 6, cursor: 'pointer', color: '#4A1E5C' },
};